import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { cn } from '../../shared/cn';

export type ErrorArea = 'application' | 'access' | 'shell' | 'navigation' | 'header' | 'content';
export type ErrorFallbackVariant = 'page' | 'sidebar' | 'header' | 'content';

export interface ErrorReport {
  readonly area: string;
  readonly detail: string;
  readonly occurredAt: string;
  readonly path: string;
  readonly reference: string;
}

interface AppErrorBoundaryProps {
  readonly area: ErrorArea;
  readonly children: ReactNode;
  readonly resetKey?: string;
  readonly variant?: ErrorFallbackVariant;
}

interface AppErrorBoundaryState {
  readonly error: unknown | null;
  readonly report: ErrorReport | null;
}

const areaLabels: Record<ErrorArea, string> = {
  application: 'Aplicacion completa',
  access: 'Acceso a la aplicacion',
  shell: 'Panel principal',
  navigation: 'Menu de navegacion',
  header: 'Barra superior',
  content: 'Contenido de la pantalla',
};

const areaCodes: Record<ErrorArea, string> = {
  application: 'APP',
  access: 'AUTH',
  shell: 'SHELL',
  navigation: 'NAV',
  header: 'TOP',
  content: 'PAGE',
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, report: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error, report: null };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const report = createErrorReport(error, this.props.area);
    this.setState({ error, report });
    console.error('Application component error', {
      area: this.props.area,
      componentStack: info.componentStack,
      error,
      reference: report.reference,
    });
  }

  componentDidUpdate(previousProps: AppErrorBoundaryProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, report: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const report = this.state.report ?? createErrorReport(this.state.error, this.props.area);
    return (
      <AppErrorFallback
        report={report}
        variant={this.props.variant ?? 'content'}
        onRetry={() => this.setState({ error: null, report: null })}
      />
    );
  }
}

export function GlobalErrorMonitor() {
  const [report, setReport] = useState<ErrorReport | null>(null);

  useEffect(() => {
    const showReport = (error: unknown) => {
      const nextReport = createErrorReport(error, 'application');
      console.error('Unhandled application error', { error, reference: nextReport.reference });
      setReport(nextReport);
    };
    const handleError = (event: ErrorEvent) => showReport(event.error ?? new Error(event.message));
    const handleRejection = (event: PromiseRejectionEvent) => showReport(event.reason);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  if (!report) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 w-[min(24rem,calc(100vw-2rem))] border border-[#efc9c0] bg-white p-4 text-[#223126] shadow-lg">
      <ErrorSummary report={report} compact />
      <div className="mt-3 flex flex-wrap gap-2">
        <FallbackButton onClick={() => window.location.reload()}>Recargar</FallbackButton>
        <FallbackButton onClick={() => setReport(null)}>Cerrar aviso</FallbackButton>
      </div>
    </div>
  );
}

export function AppErrorFallback({ report, variant, onRetry }: { report: ErrorReport; variant: ErrorFallbackVariant; onRetry: () => void }) {
  if (variant === 'sidebar') {
    return (
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-[#efc9c0] bg-white p-4 text-[#223126]">
        <ErrorSummary report={report} compact />
        <div className="mt-4 grid gap-2">
          <FallbackButton onClick={onRetry}>Reintentar menu</FallbackButton>
          <FallbackLink href="/admin/dashboard">Ir al panel</FallbackLink>
          <FallbackLink href="/admin/calendar">Ir al calendario</FallbackLink>
        </div>
      </aside>
    );
  }

  if (variant === 'header') {
    return (
      <header className="sticky top-0 z-10 border-b border-[#efc9c0] bg-[#fff8f5] px-4 py-3 text-[#223126]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ErrorSummary report={report} compact />
          <div className="flex gap-2">
            <FallbackButton onClick={onRetry}>Reintentar</FallbackButton>
            <FallbackLink href="/admin/dashboard">Panel</FallbackLink>
          </div>
        </div>
      </header>
    );
  }

  return (
    <section
      className={cn(
        'flex items-center justify-center px-4 py-8 text-[#223126]',
        variant === 'page' ? 'min-h-screen bg-[#f8faf6]' : 'min-h-[calc(100vh-8.5rem)]',
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="w-full max-w-xl border border-[#efc9c0] bg-white p-6 shadow-sm">
        {variant === 'page' ? (
          <div className="mb-5 flex items-center gap-3 border-b border-[#eef3ea] pb-5">
            <img src="/happy-bark-logo.webp" alt="Happy Bark" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#d9dfd1]" />
            <div>
              <p className="text-sm font-semibold">Happy Bark</p>
              <p className="text-xs text-[#687461]">Panel del equipo</p>
            </div>
          </div>
        ) : null}
        <ErrorSummary report={report} />
        <div className="mt-5 flex flex-wrap gap-2">
          <FallbackButton onClick={onRetry}>Reintentar</FallbackButton>
          <FallbackLink href="/admin/dashboard">Ir al panel</FallbackLink>
          <FallbackLink href="/">Pagina principal</FallbackLink>
        </div>
      </div>
    </section>
  );
}

function ErrorSummary({ report, compact = false }: { report: ErrorReport; compact?: boolean }) {
  return (
    <div className="min-w-0" role="alert">
      <p className="text-xs font-semibold uppercase text-[#b95f50]">Algo no salio bien</p>
      <h1 className={cn('mt-1 font-semibold text-[#223126]', compact ? 'text-sm' : 'text-xl')}>No pudimos cargar esta parte</h1>
      <p className="mt-2 break-words text-sm leading-5 text-[#5f6b58]">{report.detail}</p>
      <dl className={cn('mt-3 grid gap-1 text-xs text-[#687461]', !compact && 'border-t border-[#eef3ea] pt-3')}>
        <div><dt className="inline font-semibold">Area: </dt><dd className="inline">{report.area}</dd></div>
        <div><dt className="inline font-semibold">Pantalla: </dt><dd className="inline break-all">{report.path}</dd></div>
        <div><dt className="inline font-semibold">Hora: </dt><dd className="inline">{report.occurredAt}</dd></div>
        <div><dt className="inline font-semibold">Referencia: </dt><dd className="inline font-mono font-semibold text-[#4a5845]">{report.reference}</dd></div>
      </dl>
    </div>
  );
}

function FallbackButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return <button type="button" className="inline-flex h-9 items-center justify-center border border-[#75816a] bg-[#75816a] px-3 text-sm font-medium text-white hover:bg-[#65735c]" onClick={onClick}>{children}</button>;
}

function FallbackLink({ children, href }: { children: ReactNode; href: string }) {
  return <a className="inline-flex h-9 items-center justify-center border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" href={href}>{children}</a>;
}

export function createErrorReport(error: unknown, area: ErrorArea, now = new Date(), path = currentPath()): ErrorReport {
  const raw = error instanceof Error ? `${error.name}:${error.message}` : String(error);
  const stamp = formatReferenceDate(now);
  return {
    area: areaLabels[area],
    detail: friendlyErrorDetail(error),
    occurredAt: new Intl.DateTimeFormat('es-MX', { dateStyle: 'short', timeStyle: 'medium' }).format(now),
    path,
    reference: `HB-${areaCodes[area]}-${stamp}-${shortHash(raw)}`,
  };
}

export function friendlyErrorDetail(error: unknown) {
  const message = error instanceof Error ? error.message.trim() : '';
  const normalized = message.toLowerCase();
  if (normalized.includes('invalid time value') || normalized.includes('invalid date')) return 'No se pudo interpretar una fecha. Revisa la informacion de esta pantalla.';
  if (normalized.includes('failed to fetch') || normalized.includes('networkerror') || normalized.includes('network request failed')) return 'No se pudo conectar con el servicio. Revisa tu conexion e intenta nuevamente.';
  if (normalized.includes('dynamically imported module') || normalized.includes('loading chunk') || normalized.includes('chunkloaderror')) return 'No se pudo cargar una actualizacion de la aplicacion. Intenta recargar la pagina.';
  if (normalized.includes('cannot read properties') || normalized.includes('undefined') || normalized.includes('null')) return 'Falta informacion necesaria para mostrar esta parte de la pantalla.';
  if (message && message.length <= 120 && !/[{}<>]|https?:\/\/|\bat\s+\w+/i.test(message)) return message;
  return 'La aplicacion encontro informacion inesperada al mostrar esta parte.';
}

function currentPath() {
  if (typeof window === 'undefined') return 'No disponible';
  return `${window.location.pathname}${window.location.search}`;
}

function formatReferenceDate(date: Date) {
  const digits = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}${digits(date.getMonth() + 1)}${digits(date.getDate())}-${digits(date.getHours())}${digits(date.getMinutes())}`;
}

function shortHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36).toUpperCase().slice(0, 5).padStart(5, '0');
}
